<script setup lang="ts">
import { apps, Commands, formatting, Models, useOrderViewContext, useRegistry } from '@hantera/portal-app';
import { Badge, ContextMenuSections, Draggable, type DropZoneRef, IconChevron } from '@hantera/design-system';
import { computed, ref, useTemplateRef, watch } from "vue";
import DetailsOpen from "./DetailsOpen.vue";
import DetailsProcessing from "./DetailsProcessing.vue";
import DetailsCompleted from "./DetailsCompleted.vue";
import DetailsCancelled from "./DetailsCancelled.vue";
import AppSlotView from "@/AppSlotView.vue";
import Breakdown from "@/components/Order/Deliveries/OrderLine/Breakdown.vue";
import ProductSelector from "@/components/Order/Deliveries/OrderLine/ProductSelector.vue";

const props = defineProps<{
  orderLine: Models.Graph.OrderLine
  delivery: Models.Graph.Delivery
}>();

const orderViewContext = useOrderViewContext()
const registry = useRegistry();
const expanded = ref(false);
const details = useTemplateRef<HTMLDivElement>('details');
const allowDrag = computed(() => props.delivery.deliveryState === 'open')

const imageTemplate = await registry.get('portal/general/orderLineImageUrlTemplate');

const imageUrl = computed(() => {

  if (typeof imageTemplate === 'string') {
    return formatting.string(imageTemplate, {
      image: props.orderLine.image,
      productNumber: props.orderLine.productNumber,
    });
  }

  return props.orderLine.image;
});

const displayFallbackImage = ref(!props.orderLine.image);

function onImageError() {
  displayFallbackImage.value = true
}

watch(() => props.orderLine.image, () => {
  if (!props.orderLine.image) {
    displayFallbackImage.value = true;
  }
})

function toggleExpand() {
  expanded.value = !expanded.value;
}

const detailsStyle = ref<any>({});
let styleTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
watch(expanded, v => {

  if (!v) {
    clearTimeout(styleTimeout);
    detailsStyle.value = {
      maxHeight: details.value?.scrollHeight + 'px',
    };
    styleTimeout = setTimeout(() => {
      detailsStyle.value = {};
    }, 0);
    return;
  }

  detailsStyle.value = {
    maxHeight: details.value?.scrollHeight + 'px',
  };

  styleTimeout = setTimeout(() => {
    detailsStyle.value = {
      maxHeight: 'none',
    };
  }, 100);

});

async function setProduct(product: apps.standardServices.OrderProduct) {
  const commands: Commands.Order.OrderCommand[] = [{
    type: 'setOrderLineProduct',
    orderLineId: props.orderLine.orderLineId,
    productNumber: product.productNumber,
    description: product.description,
    image: product.image
  }, {
    type: 'setOrderLineUnitPrice',
    orderLineId: props.orderLine.orderLineId,
    unitPrice: product.unitPrice,
  }, {
    type: 'setOrderLineTax',
    orderLineId: props.orderLine.orderLineId,
    taxFactor: product.taxFactor,
  }];

  if (product.dynamic) {
    commands.push({
      type: 'setOrderLineDynamicFields',
      orderLineId: props.orderLine.orderLineId,
      fields: product.dynamic,
    })
  }

  await orderViewContext.pushPendingChange(
    'setProduct_' + props.orderLine.orderLineId,
    commands,
    'Set {productNumber} to order line {orderLineNumber} on {deliveryNumber}',
    {
      productNumber: product.productNumber,
      orderLineNumber: props.orderLine.orderLineNumber,
      deliveryNumber: props.delivery.deliveryNumber
    }
  )
}

async function onDropped(zone: DropZoneRef) {

  const targetDeliveryId = zone.metadata?.deliveryId;
  if (!targetDeliveryId) {
    return
  }

  const targetDelivery = orderViewContext.state.deliveries.find(r => r.deliveryId === targetDeliveryId)
  if (!targetDelivery) {
    return
  }

  const currentDelivery = orderViewContext.state.deliveries.find(r => r.orderLines.find(l => l.orderLineId === props.orderLine.orderLineId))
  if (!currentDelivery) {
    return
  }

  await orderViewContext.pushPendingChange(
    undefined,
    [{
      type: 'moveOrderLineToDelivery',
      orderLineId: props.orderLine.orderLineId,
      deliveryId: targetDelivery.deliveryId,
    }],
    'Moved order line {orderLineNumber}, {productNumber} ({quantity}) from {fromDelivery} to {toDelivery}',
    {
      orderLineNumber: props.orderLine.orderLineNumber,
      productNumber: props.orderLine.productNumber,
      quantity: props.orderLine.quantity,
      fromDelivery: currentDelivery.deliveryNumber,
      toDelivery: targetDelivery.deliveryNumber
    }
  )
}

const contextMenu = computed<ContextMenuSections>(() => {

  if (props.delivery.deliveryState !== 'open') {
    return <ContextMenuSections>[[]]
  }

  return <ContextMenuSections>[[{
    label: 'Remove order line',
    async action() {

      await orderViewContext.pushPendingChange(
        undefined,
        [{
          type: 'deleteOrderLine',
          orderLineId: props.orderLine.orderLineId,
        }],
        'Deleted order line {orderLineNumber}, {productNumber} ({quantity}) from {delivery}',
        {
          orderLineNumber: props.orderLine.orderLineNumber,
          productNumber: props.orderLine.productNumber,
          quantity: props.orderLine.quantity,
          Delivery: props.delivery.deliveryNumber,
        }
      )
    },
  }]]
})

</script>

<template>
  <Draggable
    :type="`orderline_${orderViewContext.state.orderId}`"
    :data="orderLine"
    :allow-drag="allowDrag"
    @dropped="onDropped"
    handle-selector=".drag-handle"
    class="order-line box-content"
    :class="{ 'order-line--expanded': expanded }"
    v-context-menu="contextMenu"
  >
    <div class="order-line-primary p">
      <div class="drag-handle" :class="{
        'drag-handle--disable': !allowDrag
      }">
        ⋮
      </div>
      <div class="order-line-primary-main">
        <div class="order-line-image-link box" v-if="displayFallbackImage">
          
        </div>
        <a :href="imageUrl" target="_blank" class="order-line-image-link box" v-else>
          <img
            :src="imageUrl"
            class="order-line-image"
            @error="onImageError"
          />
        </a>
        <div class="order-line-product-number">
          <ProductSelector
            class=""
            :delivery="delivery"
            :order-line="orderLine"
            @confirm="setProduct"
          />
          <Badge v-if="orderLine.releasedToInvoice">Invoice</Badge>
        </div>
        <div class="order-line-description">{{ orderLine.description }}</div>
      </div>
      <DetailsOpen
        :order-line="orderLine"
        :delivery="delivery"
        v-if="delivery.deliveryState === 'open'"
      />
      <DetailsProcessing
        :order-line="orderLine"
        v-if="delivery.deliveryState === 'processing'"
      />
      <DetailsCompleted
        :order-line="orderLine"
        v-if="delivery.deliveryState === 'completed'"
      />
      <DetailsCancelled
        :order-line="orderLine"
        v-if="delivery.deliveryState === 'cancelled' || delivery.deliveryState == 'cancelledByOrder'"
      />
      <AppSlotView
        :slot="apps.orderViewSlots.orderLine.details"
        :context="{
          orderLine,
          delivery
        }"
      />
      <div class="order-line-expand-button" @click.prevent.stop="toggleExpand">
        <IconChevron class="order-line-expand-button-arrow" />
      </div>
    </div>
    <div
      class="order-line-details"
      ref="details"
      :style="detailsStyle"
    >
      <div class="order-line-details-margin">
        <div class="order-line-extras">
          <AppSlotView
            :slot="apps.orderViewSlots.orderLine.extras"
            :context="{
              orderLine,
              delivery
            }"
          />
        </div>
        <div class="order-line-breakdown">
          <Breakdown
            :order-line="orderLine"
          />
        </div>
      </div>
    </div>
    
  </Draggable>
</template>

<style>
.drag-ghost>.order-line {
  grid-template-columns: 45px auto [middle] repeat(99, min-content) !important;
  grid-template-rows: min-content min-content;
  grid-auto-columns: min-content;
  row-gap: 5px;
}
</style>

<style scoped>
.order-line {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  --grid-column-offset: 0;
}

.order-line:hover .drag-handle {
  opacity: 1;
}

.drag-handle.drag-handle--disable {
  display: none;
}

.drag-handle {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: grab;
  width: 20px;
  z-index: 10;
  background-color: hsla(var(--color-100), .8);
  opacity: 0;
  transition: opacity 200ms;
  user-select: none;
}

.order-line--expanded .order-line-expand-button-arrow {
  transform: rotate(-180deg);
}

.order-line :deep(.panel-content__body) {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
}

.order-line-primary {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  grid-template-rows: min-content min-content;
  grid-auto-flow: column;
  align-items: center;
  position: relative;
}

.order-line-primary-main {
  display: contents;
}

.order-line-details {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;

  overflow: hidden;
  transition: max-height 0.1s ease-out;
  max-height: 0;
  box-sizing: border-box;
}

.order-line-details-margin {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  border-top: 1px solid hsl(var(--color-70));
  align-items: start;
}

.order-line-extras {
  grid-column: 1 / middle;
}

.order-line-breakdown {
  grid-column: middle / calc(var(--grid-column-offset) - 2);
  display: grid;
  grid-template-columns: auto;
}

.order-line-breakdown:has(> div) {
  padding: 5px 0;
}

.order-line-image-link {
  grid-row: span 2;
  display: block;
  padding-bottom: 100%;
  position: relative;
}

.order-line-image {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.order-line-product-number {
  padding-left: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
}

.order-line-description {
  grid-column: 2 / 3;
  grid-row: 2;
  padding-left: 5px;
}

:deep(.detail) {
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: subgrid;
  grid-row: 1 / 3;
  align-items: center;
}

:deep(.detail-label) {
  white-space: nowrap;
  font-size: 12px;
  text-align: right;
  padding-left: 10px;
  color: hsl(var(--color-text-50));
}

:deep(.detail-value) {
  white-space: nowrap;
  padding-left: 10px;
  display: inline-flex;
  justify-content: flex-end;
}

:deep(.detail-value--warning) {
  background-color: var(--color-warning);
  color: black;
}

:deep(.detail-value--success) {
  color: var(--color-success-50);
}

:deep(.detail-value input) {
  height: 22px;
}

.order-line-expand-button {
  grid-column: -2;
  grid-row: 1 / 3;
  padding: 5px;
  margin-left: 5px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  cursor: pointer;
}

.order-line-expand-button-arrow {
  width: 14px;
}

@container (max-width: 700px) {

  .order-line {
    --grid-column-offset: 1;
  }

  .order-line-primary-main {
    display: grid;
    grid-template-columns: 45px auto;
    grid-template-rows: min-content min-content;
    grid-column: 1 / -1;
    grid-row: 1 / 2;
    align-items: center;
    padding-right: 30px;
  }

  :deep(.detail) {
    display: grid;
    grid-template-columns: auto;
    grid-template-rows: subgrid;
    grid-row: 3 / 5;
    align-items: center;
  }

  .order-line-expand-button {
    grid-column: -2;
    grid-row: 1 / 2;
  }

  .order-line-breakdown:has(> div) {
    padding: 5px;
  }
}
</style>